import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { HDate } from '@hebcal/core';
import { useHalachicData } from '../../hooks/useHalachicData';
import { getLeyningData } from '../../engine/providers/leyningProvider';
import apiService from '../../sdk/apiService';
import templates from './templates';
import './MoadWidget.css';

const ZMAN_LABEL = {
  sunset:                'שקיעה',
  tzeit85deg:            'צאת כוכבים 8.5°',
  tzeit7083deg:          'צאת כוכבים 7° (ר"ת)',
  tzeit42min:            "צאת כוכבים 42 דק'",
  tzeit50min:            "צאת כוכבים 50 דק'",
  tzeit72min:            "צאת כוכבים 72 דק'",
  plagHamincha:          'פלג המנחה',
  minchaKetana:          'מנחה קטנה',
  minchaGedola:          'מנחה גדולה',
  chatzot:               'חצות',
  sofZmanBiurChametz:       'ביעור חמץ (גר"א)',
  sofZmanAchilatChametz:    'אכילת חמץ (גר"א)',
  sofZmanBiurChametzMGA:    'ביעור חמץ (מג"א 72 במע׳)',
  sofZmanAchilatChametzMGA: 'אכילת חמץ (מג"א 72 במע׳)',
  sofZmanTfilla:            'סוף זמן תפילה (גר"א)',
  sofZmanTfillaMGA:         'סוף תפילה (מג"א 90 במע׳)',
  sofZmanShma:              'סוף זמן שמע (גר"א)',
  sofZmanShmaMGA:           'סוף שמע (מג"א 90 במע׳)',
  netzHachama:              'נץ החמה',
  alotHaShachar:            'עלות השחר',
  candleLighting:           'הדלקת נרות',
  havdalah:                 'הבדלה',
  fastBegins:               'תחילת הצום',
  fastEnds:                 'סיום הצום',
  eveCandleLighting:        'הדלקת נרות',
  eveFastBegins:            'תחילת הצום',
  // וריאנטים נוספים המוחזרים מ-Hebcal ובחירים ב-MoadZmanimPerDayStep
  sunrise:                  'זריחה',
  misheyakir:               'משיכיר',
  misheyakirMachmir:        'משיכיר (מחמיר)',
  dawn:                     'עלות (דמדומי בוקר)',
  dusk:                     'בין השמשות (דמדומי ערב)',
  beinHaShmashos:           'בין השמשות',
  chatzotNight:             'חצות הלילה',
  minchaGedolaMGA:          'מנחה גדולה (מג"א)',
  minchaKetanaMGA:          'מנחה קטנה (מג"א)',
  sofZmanShmaMGA16Point1:   'סוף זמן שמע (מג"א 72 במע׳)',
  sofZmanShmaMGA19Point8:   'סוף זמן שמע (מג"א 90 במע׳)',
  sofZmanTfillaMGA16Point1: 'סוף זמן תפילה (מג"א 72 במע׳)',
  sofZmanTfillaMGA19Point8: 'סוף זמן תפילה (מג"א 90 במע׳)',
  // שיטת בעל התניא (חב"ד)
  alosBaalHatanya:          'עלות השחר (בעל התניא)',
  sofZmanShmaBaalHatanya:   'סוף זמן שמע (בעל התניא)',
  sofZmanTfilaBaalHatanya:  'סוף זמן תפילה (בעל התניא)',
  minchaGedolaBaalHatanya:  'מנחה גדולה (בעל התניא)',
  minchaKetanaBaalHatanya:  'מנחה קטנה (בעל התניא)',
  plagHaminchaBaalHatanya:  'פלג המנחה (בעל התניא)',
  tzaisBaalHatanya:         'צאת הכוכבים (בעל התניא)',
};

export const ZMAN_ICON = {
  sunset:                   'Sunset',
  tzeit85deg:               'Star',
  tzeit7083deg:             'Star',
  tzeit42min:               'Star',
  tzeit50min:               'Star',
  tzeit72min:               'Star',
  plagHamincha:             'Clock',
  minchaKetana:             'Clock',
  minchaGedola:             'Clock',
  chatzot:                  'Sun',
  sofZmanBiurChametz:       'Flame',
  sofZmanAchilatChametz:    'Flame',
  sofZmanBiurChametzMGA:    'Flame',
  sofZmanAchilatChametzMGA: 'Flame',
  sofZmanTfilla:            'BookOpen',
  sofZmanTfillaMGA:         'BookOpen',
  sofZmanShma:              'BookOpen',
  sofZmanShmaMGA:           'BookOpen',
  netzHachama:              'Sunrise',
  alotHaShachar:            'Sunrise',
  candleLighting:           'ShabbatCandles',
  havdalah:                 'Wine',
  fastBegins:               'Sunset',
  fastEnds:                 'MoonStar',
  eveCandleLighting:        'ShabbatCandles',
  eveFastBegins:            'Sunset',
};

// ── פונקציות עזר לחישוב זמנים ────────────────────────────────────────

const DEFAULT_ENTRY_ZMAN = { key: 'sunset', offset: -30 };
const DEFAULT_EXIT_ZMAN  = { key: 'tzeit85deg', offset: 0 };

const EVE_KEYS = new Set(['sunset', 'tzeit7083deg', 'tzeit85deg', 'tzeit42min', 'tzeit50min', 'tzeit72min']);
const CALENDAR_KEYS = new Set(['candleLighting', 'havdalah', 'fastBegins', 'fastEnds']);
// מפתחות אירועי-ערב (נבחרים ב-MoadZmanimPerDayStep ככניסה בערב) → שם האירוע בלוח הערב
const EVE_CALENDAR_ALIAS = { eveFastBegins: 'fastBegins', eveCandleLighting: 'candleLighting' };
const isCalendarKey = (k) => CALENDAR_KEYS.has(k) || Object.prototype.hasOwnProperty.call(EVE_CALENDAR_ALIAS, k);
const calendarEventName = (k) => EVE_CALENDAR_ALIAS[k] || k;

const autoSource = (key) => EVE_KEYS.has(key) ? 'eve' : 'day';

const resolveZman = (config, eveZmanim, dayZmanim) => {
  const cfg = config || DEFAULT_ENTRY_ZMAN;
  if (!cfg || cfg.key === 'none') return null;

  const source = autoSource(cfg.key) === 'day' ? dayZmanim : eveZmanim;
  const base = source?.[cfg.key];
  if (!base || base === '--:--') return '--:--';

  const offset = cfg.key === 'sunset' && cfg.minsBeforeSunset != null
    ? -(Math.abs(Number(cfg.minsBeforeSunset)) || 0)
    : (cfg.offset || 0);
  if (offset === 0) return base;

  try {
    const [h, m] = base.split(':').map(Number);
    let total = h * 60 + m + offset;
    total = ((total % 1440) + 1440) % 1440;
    const fh = Math.floor(total / 60);
    const fm = total % 60;
    return `${String(fh).padStart(2, '0')}:${String(fm).padStart(2, '0')}`;
  } catch {
    return base;
  }
};

const resolveEntryTime = (day, eveZmanim, dayZmanim, calendarEvents, globalEntryZman) => {
  const k = day.entryZman?.key;
  if (k === 'dayEntryTime')   return resolveZman(globalEntryZman || DEFAULT_ENTRY_ZMAN, eveZmanim, dayZmanim);
  if (k === 'dayEntrySunset') return eveZmanim?.sunset ?? '--:--';
  if (isCalendarKey(k))       return calendarEvents[calendarEventName(k)] ?? '--:--';
  return resolveZman(day.entryZman ?? DEFAULT_ENTRY_ZMAN, eveZmanim, dayZmanim);
};

const resolveExitTime = (day, eveZmanim, dayZmanim, calendarEvents, globalExitZman) => {
  const k = day.exitZman?.key;
  if (k === 'dayExitTime')   return resolveZman(globalExitZman || DEFAULT_EXIT_ZMAN, eveZmanim, dayZmanim);
  if (k === 'dayExitSunset') return dayZmanim?.sunset ?? '--:--';
  if (isCalendarKey(k))      return calendarEvents[calendarEventName(k)] ?? '--:--';
  return resolveZman(day.exitZman ?? DEFAULT_EXIT_ZMAN, eveZmanim, dayZmanim);
};

// ── קומפוננט ─────────────────────────────────────────────────────────

export const MoadWidget = ({
  widgetId,
  // Legacy props (no longer used)
  moadData: legacyMoadData,
  loading: legacyLoading,
  error: legacyError,
  dispatch: legacyDispatch,
  location,
  customTimes = [],
  days = [],
  template = 'Modern',
  title = 'מועד',
  showTitle = true,
  entryIcon = 'ShabbatCandles',
  exitIcon = 'MoonStar',
  globalEntryZman = null,
  globalExitZman = null,
  drashot = [],
  showLeyning = false,
  showHaftarah = false,
  leyningDisplayMode = 'summary',
}) => {
  const dispatch = useDispatch();

  // ── שליפה מהמנוע ההלכתי (לשנה עברית ורענון בשקיעה) ──────────────────
  const engineConfig = {
    location: {
      lat: location?.lat,
      lng: location?.lng,
      timezone: location?.timezone || location?.tzid || 'Asia/Jerusalem',
    },
    isIsrael: true,
    edah: 'ashkenaz',
  };

  const { today } = useHalachicData(engineConfig);
  const currentHebrewYear = today?.hebrewDate?.year;

  // ── state מקומי לנתוני ימי המועד ──────────────────────────────────────
  const [moadData, setMoadData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const daysKey = JSON.stringify(days);

  useEffect(() => {
    if (!currentHebrewYear || !days.length || !location?.lat || !location?.lng) return;

    let cancelled = false;
    const fetchMoadData = async () => {
      setLoading(true);
      setError(null);

      try {
        // קבע שנה: אם המועד כבר עבר → שנה הבאה
        const GRACE_DAYS = 7;
        let hebrewYear = currentHebrewYear;
        try {
          const lastDay = days[days.length - 1];
          const lastDayHd = new HDate(lastDay.hebrewDay, lastDay.hebrewMonth, currentHebrewYear);
          const lastDayGreg = lastDayHd.greg();
          const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0);
          const graceCutoff = new Date(todayMidnight.getTime() - GRACE_DAYS * 86400000);
          if (lastDayGreg < graceCutoff) hebrewYear = currentHebrewYear + 1;
        } catch {
          // fallback to current year
        }

        // טען זמנים לכל יום
        const daysData = await Promise.all(
          days.map(async (day) => {
            const dayName = day.name || day.label || `יום ${day.hebrewDay}/${day.hebrewMonth}`;
            try {
              // המרה עברי → לועזי (מקומי, בלי REST)
              const hd = new HDate(day.hebrewDay, day.hebrewMonth, hebrewYear);
              const gregDate = hd.greg();
              const gregorianDate = apiService.formatDateForApi(gregDate);

              // זמנים ליום עצמו
              const dayZmanim = await apiService.fetchZmanimForDate(location, gregorianDate);

              // זמני ערב — יום לפני
              const eveDateObj = new Date(gregDate);
              eveDateObj.setDate(eveDateObj.getDate() - 1);
              const eveDate = apiService.formatDateForApi(eveDateObj);
              const eveZmanim = await apiService.fetchZmanimForDate(location, eveDate);

              // אירועי לוח שנה (הדלקת נרות/הבדלה/צום) אם נדרשים.
              // אירועי כניסה (תחילת צום/הדלקת נרות) חלים בערב שלפני היום בצום גדול
              // ויוה"כ, אך ביום עצמו בצום קטן — לכן מושכים גם ערב וגם יום וממזגים
              // (הערב גובר), כדי לתפוס את זמן הכניסה בכל מקרה. יציאה תמיד ביום עצמו.
              const needsEntryCalendar = isCalendarKey(day.entryZman?.key);
              const needsExitCalendar  = isCalendarKey(day.exitZman?.key);
              const dayCalendarEvents = (needsEntryCalendar || needsExitCalendar)
                ? await apiService.fetchCalendarEventsForDate(location, gregorianDate)
                : {};
              const eveCalendarEvents = needsEntryCalendar
                ? await apiService.fetchCalendarEventsForDate(location, eveDate)
                : {};
              const entryCalendarEvents = { ...dayCalendarEvents, ...eveCalendarEvents };

              const entryTime = resolveEntryTime(day, eveZmanim, dayZmanim, entryCalendarEvents, globalEntryZman);
              const exitTime  = resolveExitTime(day, eveZmanim, dayZmanim, dayCalendarEvents, globalExitZman);

              // leyning is computed locally, not from API
              let dayLeyning = null;
              if (showLeyning || showHaftarah) {
                try {
                  dayLeyning = getLeyningData(hd, engineConfig.isIsrael);
                } catch { /* ignore */ }
              }

              return {
                date: gregorianDate,
                label: dayName,
                hebrewDay: day.hebrewDay,
                hebrewMonth: day.hebrewMonth,
                zmanim: dayZmanim,
                eveSunset: eveZmanim?.sunset ?? null,
                entryTime,
                exitTime,
                leyning: dayLeyning,
              };
            } catch (err) {
              console.error(`[MoadWidget] Error fetching "${dayName}":`, err);
              return {
                date: null,
                label: dayName,
                hebrewDay: day.hebrewDay,
                hebrewMonth: day.hebrewMonth,
                zmanim: null,
                entryTime: '--:--',
                exitTime: '--:--',
                leyning: null,
              };
            }
          })
        );

        if (!cancelled) {
          const data = { days: daysData };
          setMoadData(data);
          setLoading(false);
          // שתף נתונים ל-Redux כדי שציר הזמן (ZmanimWidget) יוכל לקרוא אותם
          if (widgetId) {
            dispatch({ type: 'FETCH_MOAD_SUCCESS', payload: { widgetId, data } });
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      }
    };

    fetchMoadData();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHebrewYear, daysKey, location?.lat, location?.lng]);

  // ── חישוב זמנים יחסיים ──────────────────────────────────────────────
  const calculateRelativeTime = (baseTime, offset, unit, roundTo) => {
    if (!baseTime) return '--:--';
    try {
      let timeStr = baseTime;
      if (baseTime.includes('T')) {
        const date = new Date(baseTime);
        timeStr = date.toTimeString().slice(0, 5);
      } else if (baseTime.match(/^\d{2}:\d{2}$/)) {
        timeStr = baseTime;
      } else {
        const date = new Date(baseTime);
        if (!isNaN(date.getTime())) {
          timeStr = date.toTimeString().slice(0, 5);
        } else {
          return '--:--';
        }
      }

      const [hours, minutes] = timeStr.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes;
      const offsetMinutes = unit === 'hours' ? offset * 60 : offset;
      const newTotalMinutes = totalMinutes + offsetMinutes;
      let finalMinutes = ((newTotalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);

      if (roundTo && roundTo > 0) {
        const mins = finalMinutes % 60;
        const roundedMins = Math.floor(mins / roundTo) * roundTo;
        finalMinutes = Math.floor(finalMinutes / 60) * 60 + roundedMins;
      }

      const finalHours = Math.floor(finalMinutes / 60);
      const finalMins = finalMinutes % 60;
      return `${finalHours.toString().padStart(2, '0')}:${finalMins.toString().padStart(2, '0')}`;
    } catch (err) {
      console.error('Error calculating relative time:', err);
      return '--:--';
    }
  };

  // עיבוד זמנים מותאמים אישית — לכל זמן מוצא dayData לפי dayIndex
  const effectiveMoadData = moadData || legacyMoadData;
  const processedCustomTimes = customTimes.map(ct => {
    const dayIndex = ct.dayIndex ?? 0;
    const dayData = effectiveMoadData?.days?.[dayIndex];

    if (ct.type === 'relative' && dayData) {
      const baseTimeMap = {
        daySunset: dayData.zmanim?.sunset,
        dayEntrySunset: dayData.eveSunset  ?? null,
        dayExitSunset:  dayData.zmanim?.sunset ?? null,
        dayTzeit7083: dayData.zmanim?.tzeit7083deg,
        dayTzeit85: dayData.zmanim?.tzeit85deg,
        dayTzeit42: dayData.zmanim?.tzeit42min,
        dayTzeit50: dayData.zmanim?.tzeit50min,
        dayTzeit72: dayData.zmanim?.tzeit72min,
        dayEntryTime: dayData.entryTime,
        dayExitTime: dayData.exitTime,
        dayChatzot: dayData.zmanim?.chatzot,
        daySofZmanShma: dayData.zmanim?.sofZmanShma,
        daySofZmanShmaMGA: dayData.zmanim?.sofZmanShmaMGA,
        daySofZmanTfilla: dayData.zmanim?.sofZmanTfilla,
        daySofZmanTfillaMGA: dayData.zmanim?.sofZmanTfillaMGA,
        daySofZmanBiurChametz: dayData.zmanim?.sofZmanBiurChametz,
        daySofZmanAchilatChametz: dayData.zmanim?.sofZmanAchilatChametz,
        daySofZmanBiurChametzMGA: dayData.zmanim?.sofZmanBiurChametzMGA,
        daySofZmanAchilatChametzMGA: dayData.zmanim?.sofZmanAchilatChametzMGA,
        dayMinchaGedola: dayData.zmanim?.minchaGedola,
        dayMinchaKetana: dayData.zmanim?.minchaKetana,
        dayPlagHamincha: dayData.zmanim?.plagHamincha,
        dayAlot: dayData.zmanim?.alotHaShachar,
        dayNetz: dayData.zmanim?.netzHachama,
      };
      const calculatedTime = calculateRelativeTime(
        baseTimeMap[ct.baseTime],
        ct.offset,
        ct.unit,
        ct.roundTo
      );
      return { ...ct, time: calculatedTime };
    }
    return ct;
  });

  // הוסף _sortKey לכל פריט — סדר יום עברי (ליל → בוקר → אחה"צ)
  // לזמנים יחסיים: baseTime קובע אם זה ליל/יום (מתעלם מ-moadPart ישן שעלול להיות שגוי)
  // לזמנים קבועים: moadPart קובע אם זה ליל/יום
  const EVE_BASE_KEYS = new Set(['dayEntrySunset', 'dayEntryTime']);
  const processedCustomTimesWithSort = processedCustomTimes.map(ct => {
    const itemMins = (ct.time && ct.time !== '--:--')
      ? (() => { const [h, m] = ct.time.split(':').map(Number); return isNaN(h) ? null : h * 60 + m; })()
      : null;
    let sortKey = itemMins ?? 9999;
    const isEve = ct.type === 'fixed'
      ? ct.moadPart === 'eve'
      : EVE_BASE_KEYS.has(ct.baseTime);
    if (itemMins !== null && isEve) {
      sortKey = itemMins - 1440;
    }
    return { ...ct, _sortKey: sortKey };
  });
  // מיון מראש לפי dayIndex ואז _sortKey — כך גם אם התבנית לא ממיינת, הסדר נכון
  processedCustomTimesWithSort.sort((a, b) =>
    (a.dayIndex ?? 0) - (b.dayIndex ?? 0) || a._sortKey - b._sortKey
  );

  // בנה מפת דרשות לפי אינדקס יום
  const drashotByDay = {};
  (drashot || []).forEach(d => {
    if (d.text?.trim()) drashotByDay[d.dayIndex] = d.text.trim();
  });

  const Template = templates[template] || templates['Modern'];

  const dayLabels = days.map(day => ({
    entry: day.entryZman?.key === 'none'         ? null
      : day.entryZman?.key === 'dayEntryTime'   ? 'כניסת המועד'
      : day.entryZman?.key === 'dayEntrySunset' ? 'שקיעה בכניסת המועד'
      : ZMAN_LABEL[day.entryZman?.key] || "זמן א'",
    exit: day.exitZman?.key === 'none'          ? null
      : day.exitZman?.key === 'dayExitTime'    ? 'יציאת המועד'
      : day.exitZman?.key === 'dayExitSunset'  ? 'שקיעה ביציאת המועד'
      : ZMAN_LABEL[day.exitZman?.key] || "זמן ב'",
    entryIcon: day.entryZman?.key === 'none' ? null
      : day.entryZman?.icon || ZMAN_ICON[day.entryZman?.key] || entryIcon,
    exitIcon: day.exitZman?.key === 'none' ? null
      : day.exitZman?.icon || ZMAN_ICON[day.exitZman?.key] || exitIcon,
  }));

  return (
    <Template
      moadData={effectiveMoadData}
      processedCustomTimes={processedCustomTimesWithSort}
      loading={loading}
      error={error}
      title={title}
      showTitle={showTitle}
      days={days}
      entryIcon={entryIcon}
      exitIcon={exitIcon}
      dayLabels={dayLabels}
      drashotByDay={drashotByDay}
      showLeyning={showLeyning}
      showHaftarah={showHaftarah}
      leyningDisplayMode={leyningDisplayMode}
    />
  );
};
